import { Box, Paper, Skeleton, TableBody, TableContainer, TableHead, TableRow, Typography, Button } from "@mui/material";
import React from "react";
import { List, useListContext, useRedirect } from "react-admin";

//Services
import { CuisineDishService } from "../../services/CuisineDishService";

//Common styles
import { CustomTableRowTR, FilledButton, StyledTable, TableData, TableHeader, TableTopHeader, TableWrapper, Title, WorkspaceContainer} from "../../styles/commonStyles";

//Components
import CuisineFilters from "./CuisineFIlters";


let cuisineDishService = new CuisineDishService();
const SKELETON_ROWS = 5;

const CuisineCustomTable = () => {
  const {
    data = [],
    isLoading,
  } = useListContext();

  const redirect = useRedirect();

  const exportToCSV = () => {
    if (!data || data.length === 0) {
      console.log("No data to export");
      return;
    }

    const headers = ['Id', 'Name', 'Meal Type', 'Type', 'Category', 'Image'];
    
    const csvData = data.map((row: any) => [
      row.id || '',
      row.name || '',
      row.mealType || '',
      row.type || '',
      row?.category?.name || '',
      row.image || ''
    ]);

    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.map(field => `"${field}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `cuisine_dishes_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (isLoading) {
    return (
      <TableWrapper>
        <TableTopHeader>
          <Title>Cuisine List</Title>
          <Box sx={{ display: "flex", gap: "12px" }}>
            <Button 
              variant="outlined" 
              size="small"
              onClick={() => redirect("create", "CuisineDish")}
            >
              Create
            </Button>
            <Button 
              variant="outlined" 
              size="small"
              onClick={exportToCSV}
            >
              Export
            </Button>
          </Box>
        </TableTopHeader>

        <TableContainer component={Paper} sx={{
          backgroundColor: "transparent",
          boxShadow: "none",
          overflow: "visible",
        }}>
          <StyledTable>
            <TableHead>
              <TableRow sx={{ backgroundColor: "rgba(232,235,237,0.3)" }}>
                {Array.from({ length: 7 }).map((_, i) => (
                  <TableHeader key={i}> </TableHeader>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {Array.from({ length: SKELETON_ROWS }).map((_, i) => (
                <CustomTableRowTR key={i}>
                  {Array.from({ length: 7 }).map((__, j) => (
                    <TableData key={j}>
                      <Skeleton height={28} />
                    </TableData>
                  ))}
                </CustomTableRowTR>
              ))}
            </TableBody>
          </StyledTable>
        </TableContainer>
      </TableWrapper>
    );
  }

  if (!data.length) {
    return (
      <Box py={4} textAlign="center">
        <Typography color="text.secondary">
          No cuisine dishes found
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <TableWrapper>
        <TableTopHeader>
          <Title>Cuisine List</Title>
          <Box sx={{ display: "flex", gap: "12px" }}>
            <Button 
              variant="outlined" 
              size="small"
              onClick={() => redirect("create", "CuisineDish")}
            >
              Create
            </Button>
            <Button 
              variant="outlined" 
              size="small"
              onClick={exportToCSV}
            >
              Export
            </Button>
          </Box>
        </TableTopHeader>

        <TableContainer component={Paper} sx={{
          backgroundColor: "transparent",
          boxShadow: "none",
          overflow: "visible",
        }}>
          <StyledTable>
            <TableHead>
              <TableRow sx={{ backgroundColor: "rgba(232,235,237,0.3) !important" }}>
                <TableHeader>Id</TableHeader>
                <TableHeader>Name</TableHeader>
                <TableHeader>Meal Type</TableHeader>
                <TableHeader>Type</TableHeader>
                <TableHeader>Category</TableHeader>
                <TableHeader>Image</TableHeader>
                <TableHeader>Action</TableHeader>
              </TableRow>
            </TableHead>

            <TableBody>
              {data.map((row: any) => (
                <CustomTableRowTR
                  key={row.id}
                  sx={{ cursor: "pointer" }}
                  onClick={() =>
                    redirect("show", "CuisineDish", row.id)
                  }
                >
                  <TableData>{row.id}</TableData>
                  <TableData>{row.name}</TableData>
                  <TableData>{row.mealType}</TableData>
                  <TableData>{row.type}</TableData>
                  <TableData>{row?.category?.name || "-"}</TableData>

                  <TableData>
                    {row.image ? (
                      <Box
                        component="img"
                        src={row.image}
                        alt={row.name}
                        sx={{
                          width: 80,
                          height: 60,
                          borderRadius: "6px",
                          objectFit: "cover",
                          border: "1px solid #e0e0e0",
                        }}
                        onError={(e: any) => {
                          e.target.src =
                            "/images/image-placeholder.png";
                        }}
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        N/A
                      </Typography>
                    )}
                  </TableData>

                  <TableData>
                    <FilledButton
                      size="small"
                      variant="text"
                      onClick={(e) => {
                        e.stopPropagation();
                        redirect("show", "CuisineDish", row.id);
                      }}
                    >
                      Show
                    </FilledButton>
                  </TableData>
                </CustomTableRowTR>
              ))}
            </TableBody>
          </StyledTable>
        </TableContainer>
      </TableWrapper>
    </>
  );
};



const CuisineList = (props: any) => {
  let [categories, setCategories] = React.useState<any>([]);

  React.useEffect(() => {
    getCategories();
  }, []);



  let getCategories = async () => {
    let data = await cuisineDishService.getDishCategories();
    if (data) {
      setCategories(data);
    } else {
      console.error("Failed to fetch categories");
    }
  }

  return (
    <WorkspaceContainer>
      <List {...props} actions={false} sx={{
      "& .RaLayout-content": {
        overflow: "visible",
        backgroundColor: "transparent",
      },
      "& .RaLayout-contentWithSidebar": {
        overflow: "visible",
        backgroundColor: "transparent",
      },
      "& main": {
        overflow: "visible",
        backgroundColor: "transparent",
      },
      "& .RaList-root": {
        backgroundColor: "transparent",
      },
      "& .RaList-content": {
        backgroundColor: "transparent",
      },
      overflow: "visible",
      backgroundColor: "transparent",
      "&::-webkit-scrollbar": {
        display: "none",
      },
      "-ms-overflow-style": "none",
      "scrollbar-width": "none",
    }}>
        <CuisineFilters categories={categories} />
          <br />
        <CuisineCustomTable />
      </List>

    </WorkspaceContainer>
  );
};
export default CuisineList;